
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import * as env from '@/env'
import { userFacingErrorMessage } from '@/lib/user-facing-error'

type EnterOtpMode = 'signup' | 'login'

interface EnterOtpProps {
	mode: EnterOtpMode
	email: string
	otpChallengeId: string
	onContinue: (isExistingUser: boolean) => void, 
	onBack: () => void, 
}

export function EnterOtp(props: EnterOtpProps) {
	const [isLoading, setIsLoading] = useState(false)
	const [otp, setOtp] = useState('')

	const isValidOtp = (otp: string): boolean => {
		return /^\d{6}$/.test(otp)
	}

	const handleContinue = async (): Promise<void> => {
		setIsLoading(true)
		try {
			const response = await fetch(`${env.restBaseUrl}/api/v1/verify-otp`, {
				method: 'POST',
				credentials: 'include', 
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ 
					email: props.email, 
					otp, 
					otpChallengeId: props.otpChallengeId, 
					isSignup: props.mode === 'signup'
				})
			})
			if (!response.ok) {
				//	the server's message is the whole point of the typed errors in verify-otp:
				//	"that passcode is incorrect or has expired", "too many attempts". discarding it
				//	and toasting a fixed string is what made a mistyped passcode indistinguishable
				//	from a server fault (ha-322 B3). only `message` is read, never the raw body.
				const body = await response.json().catch(() => undefined)
				throw new Error(typeof body?.message === 'string' && body.message
					? body.message
					: 'We could not verify your passcode. Please try again.')
			}

			// note the jwt token (that is the actual result of verifying the otp) 
			// is not part of the response content. 
			// instead, the server sends it in an HttpOnly cookie. 
			// (the browser will automatically store it and send it with subsequent requests.)

			// const user = await response.json()
			// setUser(user)
			//	guarded like the error-path parse above. an empty or non-JSON 2xx body throws a
			//	SyntaxError out of here, and before this guard it reached the user as the toast
			//	"Unexpected end of JSON input" (ha-617). a missing userId is NOT an error -- that
			//	is how a new user is signalled -- so only the parse itself is checked here.
			const result = await response.json().catch(() => undefined)
			if (!result) {
				console.error('verify-otp returned 2xx with a body that is not JSON')
				throw new Error('We could not verify your passcode. Please try again.')
			}
			const userId = result.userId
			const isExistingUser = !!userId

			// if (data.location) {
			// 	props.setLocation(data.location)
			// 	props.setHasValidLocation(true)
			// }
			// if (data.introduction) {
			// 	props.setIntroduction(data.introduction)
			// }

			props.onContinue(isExistingUser)
		} catch (error) {
			//	every throw in the try above carries text written for a user, so its message is
			//	shown. userFacingErrorMessage() is what keeps a platform-thrown Error from being
			//	shown alongside them -- this catch had no such filter at all before (ha-617).
			toast.error(userFacingErrorMessage(error) ?? 'We could not verify your passcode. Please try again.')
			console.error('Error verifying passcode:', error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="space-y-4">
			<label className="block text-sm text-gray-700">
				We have sent you an email with a 6-digit verification code; please enter it below. 
			</label>			
			{/* {props.mode === 'signup' && (
				<label className="block text-sm font-normal text-gray-700">
					This is to make sure that no one else can sign up using your email address. 
				</label>
			)} */}
			<label className="block text-sm font-normal text-gray-700">
				If you can't find our email, check your Spam folder. 
				If still not found, please double-check your email address in the previous step, 
				and try again.
			</label>
			<Input
				type="text"
				autoComplete="one-time-code"
				placeholder="123456"
				maxLength={6}
				value={otp}
				onChange={(e) => setOtp(e.target.value)}
			/>
			<div className="flex justify-between">
				<Button
					onClick={props.onBack}
					className="bg-red-500 hover:bg-red-600 text-white flex items-center justify-center w-24"
				>
					<ChevronLeft className="mr-2 h-4 w-4" />
					Back
				</Button>
				<Button
					onClick={handleContinue}
					disabled={!isValidOtp(otp) || isLoading}
					className="bg-green-500 hover:bg-green-600 text-white flex items-center justify-center w-32"
				>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						</>
					) : (
						<>
							Continue
							<ChevronRight className="ml-2 h-4 w-4" />
						</>
					)}
				</Button>
			</div>
		</div>
	)
}
